import React, { useState, useEffect, useCallback } from 'react';
import { Fade, TableRow, TableCell, alpha, Typography } from '@mui/material';
import { ShoppingCart as Icon } from '@mui/icons-material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2, fmtDate } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, AuditRow } from '../../services/reportService';
import { Chip } from '@mui/material';
import { Security as AuditIcon } from '@mui/icons-material';
 
const AUDIT_ACCENT = '#607D8B';
 
const AuditInfoReport: React.FC = () => {
  const [rows, setRows]     = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Record<string, string>>({
    module: '', date_from: '', date_to: '', created_by: '',
  });
 
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await reportService.getAuditReport(p);
      setRows(res.results);
    } catch {} finally { setLoading(false); }
  }, [filters]);
 
  useEffect(() => { fetchData(); }, [fetchData]);
 
  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return reportService.downloadAudit(fmt, p);
  };
 
  const moduleColors: Record<string, string> = {
    purchase: '#2196F3', selling: '#4CAF50', return: '#FF9800', cost: '#9C27B0',
  };
 
  const columns: ReportColumn[] = [
    { key: 'module',      label: 'Module',     format: v => <Chip size="small" label={v} sx={{ background: alpha(moduleColors[v] || '#607D8B', 0.12), color: moduleColors[v] || '#607D8B', fontWeight: 700, height: 20, fontSize: '0.7rem' }} /> },
    { key: 'record_id',   label: 'Record #',   align: 'center' },
    { key: 'record_date', label: 'Date',       format: v => fmtDate(v), noWrap: true },
    { key: 'quantity',    label: 'Qty',        align: 'right', format: v => v || '—' },
    { key: 'amount',      label: 'Amount (Tk.)', align: 'right', format: v => v ? fmt2(v) : '—' },
    { key: 'remarks',     label: 'Remarks',    format: v => v || '—' },
    { key: 'created_by',  label: 'Created By', format: v => <Typography variant="caption" color="text.secondary">{v}</Typography> },
    { key: 'created_at',  label: 'Created At', format: v => v ? new Date(v).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—', noWrap: true },
    { key: 'updated_by',  label: 'Updated By', format: v => v || '—' },
  ];
 
  const summaryCards: SummaryCard[] = [
    { label: 'Total Records', value: rows.length, color: AUDIT_ACCENT },
    ...(['purchase','selling','return','cost'] as const).map(m => ({
      label: `${m.charAt(0).toUpperCase() + m.slice(1)} Entries`,
      value: rows.filter(r => r.module === m).length,
      color: moduleColors[m],
    })),
  ];
 
  return (
    <Fade in timeout={400}><div>
      <ReportLayout title="Audit Information Report" subtitle="Complete activity log across all modules"
        accentColor={AUDIT_ACCENT} columns={columns} rows={rows} loading={loading}
        tableMinWidth={1500}
        summaryCards={summaryCards} onRefresh={fetchData} onDownload={handleDownload}
        emptyIcon={<AuditIcon sx={{ fontSize: 40 }} />}
        filters={
          <ReportFilterBar accentColor={AUDIT_ACCENT} values={filters}
            onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
            onClear={() => setFilters({ module: '', date_from: '', date_to: '', created_by: '' })}
            fields={[
              { type: 'select', key: 'module',     label: 'Module', options: ['purchase','selling','return','cost'].map(m => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) })), minWidth: 150 },
              { type: 'date',   key: 'date_from',  label: 'From' },
              { type: 'date',   key: 'date_to',    label: 'To' },
              { type: 'search', key: 'created_by', label: 'Created By', placeholder: 'Search user...', minWidth: 160 },
            ]}
          />
        }
      />
    </div></Fade>
  );
};
export default AuditInfoReport;